import {
  AFFILIATE_LINK_CHECK_KEY,
  AFFILIATE_LINK_KEY,
  SUSPENDED_KEY,
  SUSPENSION_DATE_KEY,
  SUSPENSION_NOTE_KEY,
  SUSPENSION_REASON_KEY,
  SUSPENSION_SOURCE_KEY,
} from '@constants/fieldKeys'
import { PropertyItem } from '@generated/graphql'
import { AffiliateLinkCheck } from '@modules/affiliateLinkCheck'
import { getPropertyAtContext, getPropertyValue } from '@modules/property'

export enum SuspensionReason {
  NoWorkingAffiliateLinks = 'no-working-affiliate-links',
  PartnerClosed = 'partner-closed',
  MarketClosed = 'market-closed',
  ContractEnded = 'contract-ended',
  Compliance = 'compliance',
  Other = 'other',
}

export enum SuspensionSource {
  Manual = 'manual',
  AffiliateLinkCheck = 'affiliate-link-check',
}

export const SUSPENSION_REASON_LABELS: Record<SuspensionReason, string> = {
  [SuspensionReason.NoWorkingAffiliateLinks]: 'No working affiliate links',
  [SuspensionReason.PartnerClosed]: 'Partner closed',
  [SuspensionReason.MarketClosed]: 'Market closed (GEO block)',
  [SuspensionReason.ContractEnded]: 'Contract ended',
  [SuspensionReason.Compliance]: 'Compliance',
  [SuspensionReason.Other]: 'Other',
}

export const SUSPENSION_SOURCE_LABELS: Record<SuspensionSource, string> = {
  [SuspensionSource.Manual]: 'Manual',
  [SuspensionSource.AffiliateLinkCheck]: 'Affiliate link check',
}

export const SUSPENSION_REASON_OPTIONS = Object.values(SuspensionReason).map(
  value => ({ value, label: SUSPENSION_REASON_LABELS[value] }),
)

export const SUSPENSION_SOURCE_OPTIONS = Object.values(SuspensionSource).map(
  value => ({ value, label: SUSPENSION_SOURCE_LABELS[value] }),
)

/** Detail fields rendered next to the Suspended checkbox, not on their own. */
export const SUSPENSION_DETAIL_KEYS = [
  SUSPENSION_REASON_KEY,
  SUSPENSION_NOTE_KEY,
  SUSPENSION_DATE_KEY,
  SUSPENSION_SOURCE_KEY,
]

export const getSuspensionReasonLabel = (reason?: string) =>
  reason ? SUSPENSION_REASON_LABELS[reason as SuspensionReason] || reason : ''

export const getSuspensionSourceLabel = (source?: string) =>
  source ? SUSPENSION_SOURCE_LABELS[source as SuspensionSource] || source : ''

/** `Suspended – No working affiliate links`, or just `Suspended`. */
export const getSuspensionLabel = (reason?: string) => {
  const reasonLabel = getSuspensionReasonLabel(reason)
  return reasonLabel ? `Suspended – ${reasonLabel}` : 'Suspended'
}

export interface IProviderSuspension {
  suspended: boolean
  reason?: string
  note?: string
  date?: string
  source?: string
}

interface IPropertyContext<T> {
  properties?: T[]
  networkId?: string
  companyId?: string
  verticalId?: string
  siteId?: string
}

/**
 * The suspension fields are scoped like `suspended` itself, which is
 * site-scoped on most networks, so the site row wins when there is one.
 */
const readValue = <T extends Partial<PropertyItem>>(
  key: string,
  { properties, networkId, companyId, verticalId, siteId }: IPropertyContext<T>,
) => {
  const atSite = getPropertyAtContext({ key, properties, contextId: siteId })
  if (atSite) return atSite.data?.value
  return getPropertyValue({
    key,
    properties,
    networkId,
    companyId,
    verticalId,
  })
}

export const getProviderSuspension = <T extends Partial<PropertyItem>>(
  context: IPropertyContext<T>,
): IProviderSuspension => {
  const suspended = !!readValue(SUSPENDED_KEY, context)
  if (!suspended) return { suspended }

  return {
    suspended,
    reason: readValue(SUSPENSION_REASON_KEY, context) || undefined,
    note: readValue(SUSPENSION_NOTE_KEY, context) || undefined,
    date: readValue(SUSPENSION_DATE_KEY, context) || undefined,
    source: readValue(SUSPENSION_SOURCE_KEY, context) || undefined,
  }
}

export const getAffiliateLinkCheck = <T extends Partial<PropertyItem>>(
  context: IPropertyContext<T>,
): AffiliateLinkCheck | undefined => {
  const value = readValue(AFFILIATE_LINK_CHECK_KEY, context)
  return value && typeof value === 'object' ? value : undefined
}

/** Slug of the LMS link saved on the provider (`affiliate-link`). */
export const getAffiliateLinkSlug = <T extends Partial<PropertyItem>>(
  context: IPropertyContext<T>,
): string | undefined => {
  const value = readValue(AFFILIATE_LINK_KEY, context)
  return typeof value?.slug === 'string' ? value.slug : undefined
}
